/**
 * What the viewer may change on the ACTIVE scenario (SE-8356, PV16 / AC2,
 * §5) — the one answer every edit surface outside the Pitch reads: the Sign
 * CTA (table, columns, cards), the per-player Action menu, the editable money
 * cells and the Actions overview's Edit / Remove. The Pitch reads the same
 * rule through `useScenarioPitchSharing`, which adds the "Shared by" line.
 *
 * A VIEWER gets no edit control at all — hidden, not disabled — and an
 * EDITOR keeps Sign / Action / planning but never rename, delete or Share.
 * Current Squad keeps today's affordances; an unresolved role fails closed
 * unless the row itself says I own it or it is legacy no-owner; the backend
 * remains the rule (`SCENARIO_FORBIDDEN_VIEWER` is still rendered verbatim
 * if a write ever reaches it).
 * @module features/squad-planning/hooks/useActiveScenarioPermissions
 */
import { useMemo } from "react";

// Leaf import: the `@/features/auth` barrel drags `expo-device` into the
// module graph, which the squad-planning jest suites do not shim.
import { useCurrentUser } from "@/features/auth/providers/CurrentUser";
import { useScenarioAccess } from "@/features/squad-planning/hooks/useScenarioAccess";
import { useScenarios } from "@/features/squad-planning/stores/scenarioStore";
import {
  resolveScenarioPermissions,
  type ScenarioPermissions,
  type ScenarioRoleValue,
} from "@/features/squad-planning/utils/scenarioAccess";

/** Permissions on the active scenario, plus what finance the viewer receives. */
export interface ActiveScenarioPermissions extends ScenarioPermissions {
  /**
   * Whether the scenario's financial payload is served to the viewer: the
   * owner, a legacy no-owner scenario and an unknown role read what their tier
   * allows; an invitee reads it only while the owner's Financials switch is on
   * (PRD-6.3, D09). The backend already nulls the payload — this is what the
   * UI keys "no invented figure, no edit on hidden finance" on.
   */
  readonly canSeeFinancials: boolean;
  /** The role the API reported for the active scenario, or null. */
  readonly myRole: ScenarioRoleValue | null;
}

/**
 * Resolves the viewer's permissions on the active scenario.
 * @returns The permissions; today's affordances on Current Squad / unknown
 */
export function useActiveScenarioPermissions(): ActiveScenarioPermissions {
  // `activeId` is the selection; `activeScenario` is that id resolved against
  // the list read. While the list is loading or no longer carries the id the
  // second is null and the first is not — and that beat must read as a
  // scenario whose role is unresolved (fail closed), never as Current Squad.
  const { activeId: activeScenarioId, activeScenario } = useScenarios();
  const currentUser = useCurrentUser();
  const access = useScenarioAccess(activeScenarioId);
  const myRole = access?.myRole ?? null;
  const isLegacyNoOwner = activeScenario?.isLegacyNoOwner === true;
  const financialsVisible = access?.financialsVisible ?? false;
  // Known from the list row alone, so the owner keeps their affordances
  // while the role read is loading or failed (fail closed for everyone else).
  const isOwnedByMe =
    activeScenario?.ownerUserId != null &&
    activeScenario.ownerUserId === currentUser?.id;

  return useMemo((): ActiveScenarioPermissions => {
    // Current Squad has no owner and no role: it keeps today's affordances.
    const permissions =
      activeScenarioId == null
        ? { canEdit: true, canManage: true, canShare: false }
        : resolveScenarioPermissions({ myRole, isLegacyNoOwner, isOwnedByMe });
    const isInvitee =
      !isLegacyNoOwner && (myRole === "EDITOR" || myRole === "VIEWER");
    return {
      ...permissions,
      canSeeFinancials: !isInvitee || financialsVisible,
      myRole,
    };
  }, [
    activeScenarioId,
    myRole,
    isLegacyNoOwner,
    isOwnedByMe,
    financialsVisible,
  ]);
}
